import { P } from 'components'
import CardPin from 'components/CardPin'
import LoadingAnimation from 'components/LoadingAnimation'
import { recaptchaKey } from 'configs'
import {
  useResetPinMutation,
  useSendPinOtpMutation,
  useVerifyPINOtpMutation,
} from 'features/public-views/login-new/loginNewApiSlice'
import { validateEmail } from 'helpers'
import useRecaptchaV3 from 'hooks/useRecaptchaV3'
import { useSnackbar } from 'notistack'
import { useEffect, useState } from 'react'
import { ArrowLeft } from 'react-feather'
import { useSelector } from 'react-redux'
import { useNavigate } from 'react-router-dom'
import { CardBody, ListGroup, ListGroupItem } from 'reactstrap'
import { useChangePinMutation } from '../settingApiSlice'

const PinSetting = () => {
  const recaptcha = useRecaptchaV3(recaptchaKey)
  const navigate = useNavigate()

  const { enqueueSnackbar } = useSnackbar()
  const { identity } = useSelector((s) => s.auth)
  const [showPin, setShowPin] = useState(false)
  const [showOldPin, setShowOldPin] = useState(false)
  const [showConfirmPin, setShowConfirmPin] = useState(false)
  const [pin, setPin] = useState('')
  const [oldPin, setOldPin] = useState('')
  const [confirmPin, setConfirmPin] = useState('')
  const [isMatch, setIsMatch] = useState(false)
  const [hasType, setHasType] = useState(false)
  const [loading, setLoading] = useState(false)
  const [isLoadingForgotPin, setLoadingForgotPin] = useState(false)
  const [errMsg, setErrMsg] = useState('')
  const [changePin, { isLoading }] = useChangePinMutation()
  const [sendPinOtp] = useSendPinOtpMutation()
  const [verifyPINOtp] = useVerifyPINOtpMutation()
  const [resetPin] = useResetPinMutation()
  const [step, setStep] = useState(1)

  const togglePinVisibility = () => {
    setShowPin(!showPin)
  }

  const toggleOldPinVisibility = () => {
    setShowOldPin(!showOldPin)
  }

  const toggleConfirmPinVisibility = () => {
    setShowConfirmPin(!showConfirmPin)
  }

  const handleChangeOldPass = (e) => {
    const value = e.target.value

    setOldPin(value)
    setErrMsg('')
  }

  const handleChangeNewPass = (e) => {
    setPin(e.target.value)
    setErrMsg('')
  }

  const handleConfirmPass = (e) => {
    setConfirmPin(e.target.value)
    setErrMsg('')
  }

  const handleChangePin = async () => {
    try {
      const payload = {
        newpin: pin,
        oldpin: oldPin,
      }
      if (isMatch) {
        setLoading(true)
        const res = await changePin(payload)

        if (res.data?.success) {
          setPin('')
          setOldPin('')
          setConfirmPin('')
          enqueueSnackbar('Ganti PIN berhail', { variant: 'success' })
          window.location.replace('/pengaturan-profile')
        } else {
          throw res
        }
      }
    } catch (error) {
      enqueueSnackbar(
        error?.error?.data?.meta?.message ||
          error?.message ||
          error?.data?.message ||
          error?.data?.error?.message ||
          error?.error?.data?.meta?.error ||
          error?.error?.data?.message ||
          'Terjadi kesalahan',
        {
          variant: 'error',
        },
      )
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    if (
      oldPin?.length === 6 &&
      confirmPin?.length === 6 &&
      pin?.length === 6 &&
      pin === confirmPin
    ) {
      setIsMatch(true)
    } else {
      setIsMatch(false)
    }

    if (oldPin !== '' && pin !== '' && confirmPin !== '') {
      setHasType(true)
    } else {
      setHasType(false)
    }
  }, [oldPin, pin, confirmPin])

  const getStoredBrowserData = () => ({
    browserHash: localStorage.getItem('browserHash') || null,
    browserFingerPrint: localStorage.getItem('browserFingerPrint') || null,
    browserCustomFingerPrint:
      localStorage.getItem('browserCustomFingerPrint') || null,
    deviceHash: localStorage.getItem('PMB_deviceHash') || null,
    fcmToken: localStorage.getItem('PMB_fcmToken') || null,
    mobileVersion: localStorage.getItem('PMB_mobileVersion') || null,
    packageName: localStorage.getItem('PMB_packageName') || null,
  })

  const onFetchOtp = async () => {
    setLoadingForgotPin(true)
    let storedData = getStoredBrowserData()
    try {
      const res = await sendPinOtp({
        ...storedData,
        identity: identity,
        type: 'resetPin',
        channel: validateEmail(identity) ? 'EMAIL' : 'WHATSAPP',
        'g-recaptcha-response': await recaptcha(),
      })
      if (res?.error?.data?.error) {
        throw res
      } else {
        navigate('/otp', {
          state: {
            type: 'resetPin',
            identity: identity,
            countDown: res?.data?.expireTime,
            status: 'resetPin',
            ...storedData,
          },
        })
      }
      setLoadingForgotPin(false)
    } catch (error) {
      setLoadingForgotPin(false)
      enqueueSnackbar(
        error?.error?.data?.meta?.message ||
          error?.message ||
          error?.data?.message ||
          error?.data?.error?.message ||
          error?.error?.data?.meta?.error ||
          error?.error?.data?.message ||
          'Terjadi kesalahan',
        {
          variant: 'error',
        },
      )
    }
  }

  const renderTitle = () => {
    if (step === 1) return 'Masukkan PIN Lama'
    if (step === 2) return 'Buat PIN Baru'
    return 'Konfirmasi PIN Baru'
  }

  const onHandleSubmit = (pinInput) => {
    if (step === 1) {
      setOldPin(pinInput)
      setStep(2)
    } else if (step === 2) {
      setPin(pinInput)
      setStep(3)
    } else if (step === 3) {
      setConfirmPin(pinInput)
      if (pin === pinInput) {
        handleChangePin()
      } else {
        enqueueSnackbar('PIN baru dan konfirmasi tidak cocok', {
          variant: 'error',
        })
      }
    }
  }

  return (
    <CardBody>
      <ListGroup flush>
        <ListGroupItem className="py-4">
          <div className="d-flex justify-content-between align-items-center">
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <ArrowLeft
                size={20}
                style={{ cursor: 'pointer' }}
                onClick={() => {
                  if (step > 1) {
                    setStep((prevState) => prevState - 1)
                  } else {
                    navigate(-1)
                  }
                }}
              />
              <div>
                <P size="xlarge">Atur ulang PIN</P>
                {/* {isLoadingForgotPin ? (
                  <LoadingAnimation size={'sm'} color={'primary'} />
                ) : (
                  <div style={{ cursor: 'pointer' }} onClick={onFetchOtp}>
                    <P color={'#006386'}>Lupa pin?</P>
                  </div>
                )} */}
              </div>
            </div>
            {isLoadingForgotPin ? (
              <LoadingAnimation size={'sm'} color={'primary'} />
            ) : null}
          </div>
        </ListGroupItem>
        <div style={{ backgroundColor: 'white' }}>
          <CardPin
            testIdPrefix="setting-pin"
            title={renderTitle()}
            length={6}
            loading={isLoading}
            optionToShowPin={true}
            withButton={true}
            handleSubmit={(pin) => onHandleSubmit(pin)}
            isBack={false}
            showResetPin
            minHeight="85vh"
            inputType={'password'}
            navigateScreen={'/pengaturan-profile'}
            removePin={step < 3 ? true : false}
          />
        </div>
      </ListGroup>
    </CardBody>
  )
}

export default PinSetting
